/* Sağ panelin form denetimleri — editörün ayarlar sütunu bunlardan kuruluyor.

   Her denetim değeri alıyor ve değişince `set`'i çağırıyor; belgeyi
   kendisi bilmiyor. Metin kutuları `change`'de yazıyor (her tuşta değil):
   her yazım önizlemeyi yeniden çizer ve kaydırıcıya kadar her şey titrerdi.
   Renk ve kaydırıcı `input`'ta yazıyor — onlar yalnızca CSS'i değiştiriyor
   ve önizleme yeniden çizilmeden güncelleniyor. */

import type { Media } from '@frawgraphy/shared';
import { photos, type PhotoRow } from '../api.ts';
import { el, replace } from '../dom.ts';
import { contrast } from '../design/defaults.ts';
import { PREVIEW_BASE } from './page.ts';

const ARCHIVE_BASE = 'https://photos.frawgraphy.com/browse/';

/** Bugünkü sitenin kendi dosyaları — arşiv dolana kadar seçilebilir olanlar. */
export const LEGACY_IMAGES: { src: string; width: number; height: number; label: string }[] = [
  ...[1, 2, 3, 4, 5, 6].map((n) => ({ src: `assets/img/work-${n}-900.jpg`, width: 900, height: 1260, label: `İş ${n}` })),
  ...[1, 2, 3, 4, 5, 6].map((n) => ({ src: `assets/img/ed26-${n}-900.jpg`, width: 900, height: 1350, label: `Editorial 26 / ${n}` })),
  ...[1, 2, 3].map((n) => ({ src: `assets/img/ai-${n}-900.jpg`, width: 900, height: 1205, label: `Yapay zekâ ${n}` })),
  { src: 'assets/img/studio-1100.jpg', width: 1100, height: 1374, label: 'Stüdyo' },
  { src: 'assets/img/hero.jpg', width: 1280, height: 853, label: 'Giriş karesi' }
];

export function row(label: string, ...controls: (Node | null)[]): HTMLElement {
  return el('div', { class: 'design-row' }, el('span', { class: 'design-label', text: label }), ...controls);
}

export function group(title: string, ...children: (Node | null | false)[]): HTMLElement {
  return el('section', { class: 'design-group' }, el('h3', { text: title }), ...children);
}

export function colorField(label: string, value: string | undefined, fallback: string, set: (v: string | undefined) => void, onToast: (m: string) => void): HTMLElement {
  const picker = el('input', { type: 'color', 'aria-label': label });
  picker.value = value ?? fallback;
  const hex = el('input', { type: 'text', class: 'design-hex', placeholder: fallback, 'aria-label': `${label} (hex)`, maxlength: 7 });
  hex.value = value ?? '';
  picker.addEventListener('input', () => {
    hex.value = picker.value;
    set(picker.value);
  });
  hex.addEventListener('change', () => {
    const v = hex.value.trim().toLowerCase();
    if (!v) return set(undefined);
    if (/^#[0-9a-f]{6}$/.test(v)) {
      picker.value = v;
      set(v);
    } else {
      onToast('Renk #rrggbb biçiminde olmalı.');
    }
  });
  const reset = el('button', { type: 'button', text: '×', title: 'Varsayılana dön', disabled: !value, onclick: () => set(undefined) });
  return row(label, picker, hex, reset);
}

export function rangeField(label: string, value: number, min: number, max: number, set: (v: number) => void, step = 0.05): HTMLElement {
  const out = el('output', { text: `%${Math.round(value * 100)}` });
  const input = el('input', { type: 'range', min, max, step, 'aria-label': label });
  input.value = String(value);
  input.addEventListener('input', () => {
    const v = Math.round(Number(input.value) * 100) / 100;
    out.textContent = `%${Math.round(v * 100)}`;
    set(v);
  });
  return row(label, input, out);
}

export function selectField(label: string, options: [string, string][], value: string, set: (v: string) => void): HTMLElement {
  const select = el('select', { 'aria-label': label }, ...options.map(([v, t]) => el('option', { value: v, text: t, selected: v === value })));
  select.addEventListener('change', () => set(select.value));
  return row(label, select);
}

export function toggleField(label: string, checked: boolean, set: (v: boolean) => void): HTMLElement {
  const box = el('input', { type: 'checkbox', checked, 'aria-label': label });
  box.addEventListener('change', () => set(box.checked));
  return el('label', { class: 'design-toggle' }, box, el('span', { text: label }));
}

export function textField(label: string, value: string, placeholder: string, set: (v: string) => void): HTMLElement {
  const input = el('input', { type: 'text', placeholder, 'aria-label': label });
  input.value = value;
  input.addEventListener('change', () => set(input.value.trim()));
  return el('div', { class: 'design-field' }, el('span', { class: 'design-label', text: label }), input);
}

export function contrastNote(bg: string, fg: string): HTMLElement {
  const ratio = contrast(bg, fg);
  return el('p', {
    class: ratio >= 4.5 ? 'dim' : 'bad',
    text: ratio >= 4.5 ? `Kontrast ${ratio.toFixed(1)}:1 — okunur.` : `✕ Kontrast ${ratio.toFixed(1)}:1 — metin için en az 4,5:1 önerilir.`
  });
}

/* ── Görsel seçici ──────────────────────────────────────── */

let archive: PhotoRow[] | null = null;

async function loadArchive(): Promise<PhotoRow[]> {
  if (!archive) archive = (await photos.list({ limit: 200 })).photos.filter((p) => p.status === 'ready');
  return archive;
}

export function mediaUrl(media: Media | undefined): string | null {
  if (!media) return null;
  if (media.photo) return `${ARCHIVE_BASE}${encodeURIComponent(media.photo)}.webp`;
  if (media.src) return media.src.startsWith('http') ? media.src : `${PREVIEW_BASE}${media.src}`;
  return null;
}

/**
 * Görsel seçici: önizleme, "Arşivden" ve "Sitedeki dosyalar" sekmeleri.
 * Arşiv R2'deki kareler (Kareler ekranından yüklenenler); sitedeki dosyalar
 * bugün yayında olan, arşive taşınmamış kareler.
 */
export function mediaField(label: string, media: Media | undefined, set: (m: Media | undefined) => void, onError: (e: unknown) => void, allowEmpty = true): HTMLElement {
  const box = el('div', { class: 'design-media' });
  const url = mediaUrl(media);
  const grid = el('div', { class: 'design-picker' });
  const tab = (name: string, run: () => void) => el('button', { type: 'button', text: name, onclick: run });

  const showLegacy = () =>
    replace(
      grid,
      ...LEGACY_IMAGES.map((img) =>
        el(
          'button',
          { type: 'button', title: img.label, onclick: () => set({ src: img.src, width: img.width, height: img.height, alt: media?.alt ?? { tr: '' } }) },
          el('img', { src: `${PREVIEW_BASE}${img.src}`, alt: img.label, loading: 'lazy' })
        )
      )
    );
  const showArchive = async () => {
    replace(grid, el('p', { class: 'dim', text: 'Okunuyor…' }));
    try {
      const rows = await loadArchive();
      if (rows.length === 0) {
        replace(grid, el('p', { class: 'dim', text: 'Arşivde hazır kare yok — Kareler ekranından yükleyin.' }));
        return;
      }
      replace(
        grid,
        ...rows.map((p) =>
          el(
            'button',
            { type: 'button', title: p.id, onclick: () => set({ photo: p.id, width: p.width, height: p.height, alt: media?.alt ?? { tr: p.alt.tr } }) },
            el('img', { src: `${ARCHIVE_BASE}${encodeURIComponent(p.id)}.webp`, alt: p.alt.tr || p.id, loading: 'lazy' })
          )
        )
      );
    } catch (err) {
      onError(err);
    }
  };

  const alt = el('input', { type: 'text', placeholder: 'Görme engelliler için kısa açıklama', 'aria-label': 'Alt metin' });
  alt.value = media?.alt?.tr.replace(/&amp;/g, '&') ?? '';
  alt.addEventListener('change', () => media && set({ ...media, alt: { ...(media.alt ?? { tr: '' }), tr: alt.value } }));

  replace(
    box,
    url ? el('img', { src: url, alt: '' }) : el('div', { class: 'design-media-empty', text: 'Görsel yok' }),
    el('div', { class: 'row' }, tab('Sitedeki dosyalar', showLegacy), tab('Arşivden', () => void showArchive()), allowEmpty && media ? el('button', { type: 'button', text: 'Kaldır', onclick: () => set(undefined) }) : null),
    grid,
    media ? el('div', { class: 'design-field' }, el('span', { class: 'design-label', text: 'Alt metin' }), alt) : null
  );
  return el('div', { class: 'design-field' }, el('span', { class: 'design-label', text: label }), box);
}
